import {
  Auth,
  Client,
  type ClientHeaders,
  type Config
} from "@medusajs/js-sdk";
import { Store } from "./sdk";

export type { ClientHeaders } from "@medusajs/js-sdk";
export { FetchError } from "@medusajs/js-sdk";
export type { StoreToggleFavoriteResponse } from "./sdk/favorites";
export type {
  StoreOrderHistoryListParams,
  StoreOrderHistoryListResponse,
  StoreOrderHistoryOrder,
  StorePreviousProductIdsParams,
  StorePreviousProductIdsResponse
} from "./sdk/order-history";
export type {
  StoreReorderLine,
  StoreReorderLineSelection,
  StoreReorderLineStatus,
  StoreReorderRequest,
  StoreReorderResponse,
  StoreReorderUnavailableReason
} from "./sdk/reorder";
export type {
  StoreAddSavedListToCartResponse,
  StoreSavedListDeleteResponse,
  StoreSavedListLineSelection,
  StoreSavedListResponse,
  StoreSavedListSummary,
  StoreSavedListsListQuery,
  StoreSavedListsResponse
} from "./sdk/saved-list";
export type {
  StoreBulkUpsertStockTracking,
  StoreStockReorderLine,
  StoreStockReorderRequest,
  StoreStockReorderResponse,
  StoreStockTrackingBulkResponse,
  StoreStockTrackingCandidate,
  StoreStockTrackingCandidatesListQuery,
  StoreStockTrackingCandidatesResponse,
  StoreStockTrackingItem,
  StoreStockTrackingItemResponse,
  StoreStockTrackingListQuery,
  StoreStockTrackingListResponse,
  StoreStockTrackingVariant,
  StoreUntrackStockTrackingResponse,
  StoreUpsertStockTracking
} from "./sdk/stock-tracking";
export type {
  StoreSupplierProduct,
  StoreSupplierProductVariant
} from "./sdk/supplier-catalog";
export type {
  StoreVoiceMessageResponse,
  StoreVoiceOrderAmbiguousItem,
  StoreVoiceOrderConfirmRequest,
  StoreVoiceOrderConfirmResponse,
  StoreVoiceOrderConfirmSelection,
  StoreVoiceOrderExactItem,
  StoreVoiceOrderItem,
  StoreVoiceOrderMatchType,
  StoreVoiceOrderNotFoundItem,
  StoreVoiceOrderParseResponse,
  StoreVoiceOrderProductOption,
  StoreVoiceOrderResolvedProduct
} from "./sdk/voice-order";
export * from "./types";
export * from "./utils";

export function resolveSpecialHeaders(): Record<string, string> {
  if (import.meta.env.VITE_X_DEV_KEY) {
    return {
      "X-Dev-Key": import.meta.env.VITE_X_DEV_KEY
    };
  }

  return {};
}

/** The Medusa backend's base URL, as the SDK below is configured with it. */
const BACKEND_URL: string = import.meta.env.VITE_MEDUSA_BACKEND_URL || "/";

/**
 * An absolute-or-root-relative URL for a store route on the same backend the
 * SDK talks to. For the one caller that cannot go through `sdk.client.fetch`:
 * the order assistant's chat stream, which needs a streamed response body and
 * error bodies (`{ error: { code, ... } }`) that the SDK's fetch would reduce to
 * a message string. Plain JSON calls (the assistant's status check included) use
 * `sdk.client.fetch`.
 */
export function storeUrl(path: string): string {
  return `${BACKEND_URL.replace(/\/+$/, "")}${path}`;
}

/**
 * The headers the SDK adds to every store request apart from the customer JWT
 * (which `sdk.client.getToken()` supplies): the publishable key and the Dev
 * gate key. The SDK's own config below is built from this same function, so a
 * raw `fetch` cannot drift from it.
 */
export function resolveStoreRequestHeaders(): Record<string, string> {
  const publishableKey = import.meta.env.VITE_MEDUSA_PUBLISHABLE_KEY;
  return {
    ...(publishableKey ? { "x-publishable-api-key": publishableKey } : {}),
    ...resolveSpecialHeaders()
  };
}

class SalonStockerSDK {
  public client: Client;

  public store: Store;
  public auth: Auth;

  constructor(config: Config) {
    this.client = new Client(config);
    this.store = new Store(this.client);
    this.auth = new Auth(this.client, config);
  }
}

const sdk = new SalonStockerSDK({
  baseUrl: BACKEND_URL,
  debug: import.meta.env.DEV,
  // The publishable key and the Dev gate key travel as plain global headers,
  // from the same resolver the order assistant's raw `fetch` uses, so the two
  // cannot drift. (The SDK's own `publishableKey` option only sets this header.)
  globalHeaders: resolveStoreRequestHeaders(),
  auth: {
    type: "jwt"
  }
  // logger // TODO logging
});

export default sdk;
